import React from 'react';
import { useGameStore } from '../store/gameStore';
import { ACTION_DETAILS } from './ActionPanel';
import { X, BookOpen, Shield, Radio, Users, Mic, Zap, HeartHandshake, Cpu, Terminal, Brain, Ghost } from 'lucide-react';

const ICONS: Record<string, any> = {
  Secure: Shield,
  Broadcast: Radio,
  Engage: Users,
  Listen: Mic,
  Jam: Zap,
  Assist: HeartHandshake,
  DeployTech: Cpu,
  Hack: Terminal,
  Influence: Brain,
  Decoy: Ghost
};

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  const currentScenario = useGameStore(state => state.currentScenario);

  if (!isOpen) return null;

  const allowedActions = currentScenario?.allowedActions || Object.keys(ACTION_DETAILS);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-800/50">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-ops-blue" />
            <h2 className="text-xl font-bold text-white tracking-widest">FIELD MANUAL</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {allowedActions.map(actionId => {
            const details = ACTION_DETAILS[actionId];
            const Icon = ICONS[actionId] || Shield;
            
            if (!details) return null;

            return (
              <div key={actionId} className="bg-slate-800/50 p-4 rounded-xl border border-slate-700 hover:border-ops-blue/50 transition-colors">
                <div className="flex items-center gap-3 mb-3 border-b border-slate-700/50 pb-2">
                  <div className="p-2 bg-slate-900 rounded-lg">
                    <Icon className="w-5 h-5 text-ops-blue" />
                  </div>
                  <h3 className="font-bold text-white text-lg">{actionId.toUpperCase()}</h3>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="grid grid-cols-[80px_1fr] gap-2">
                    <span className="text-slate-500 font-mono uppercase text-xs pt-1">Task</span>
                    <span className="text-slate-200">{details.task}</span>
                  </div>
                  <div className="grid grid-cols-[80px_1fr] gap-2">
                    <span className="text-slate-500 font-mono uppercase text-xs pt-1">Purpose</span>
                    <span className="text-slate-200">{details.purpose}</span>
                  </div>
                  <div className="grid grid-cols-[80px_1fr] gap-2">
                    <span className="text-slate-500 font-mono uppercase text-xs pt-1">Method</span>
                    <span className="text-slate-200">{details.method}</span>
                  </div>
                  <div className="grid grid-cols-[80px_1fr] gap-2">
                    <span className="text-slate-500 font-mono uppercase text-xs pt-1">End State</span>
                    <span className="text-ops-green font-bold">{details.state}</span>
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-700/30 text-xs text-slate-500 font-mono italic">
                    Ref: {details.citation}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
